// Create-mirror-client form (Debt Lookup). After success the SPA writes the
// plaintext X-API-Key to sessionStorage so the detail page can re-display it.
//
// Mirrors pages/ClientNew.tsx with the pricing inputs stripped — the mirror
// only has client_name + rps_cap. The one-time-key UX (copy-to-clipboard,
// "visible until you sign out") is copied verbatim from the GTI flow.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  api,
  type MirrorClientCreateRequest,
  type MirrorClientCreateResponse,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  FieldLabel,
  inputStyle,
  PageHeader,
  tokens,
} from "../lib/theme";

const DEFAULT_FORM: Required<MirrorClientCreateRequest> = {
  client_name: "",
  rps_cap: 50,
};

export default function MirrorClientNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState<Required<MirrorClientCreateRequest>>(
    DEFAULT_FORM,
  );
  const [created, setCreated] = useState<MirrorClientCreateResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // No on-unmount wipe — sessionStorage holds the plaintext for the same
  // client's detail page. Wiped on logout/tab close.
  useEffect(() => undefined, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.createMirrorClient(form);
      setCreated(res);
      try {
        sessionStorage.setItem(
          `loop-rtb:mirror-apikey:${res.client_id}`,
          JSON.stringify({ value: res.api_key_plaintext, ts: Date.now() }),
        );
      } catch {
        /* sessionStorage may be unavailable */
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.api_key_plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked */
    }
  };

  if (created) {
    return (
      <div>
        <PageHeader
          title="Mirror client created"
          subtitle={
            <span style={{ fontFamily: tokens.font.mono }}>
              {created.client_id}
            </span>
          }
        />
        <Card>
          <div
            style={{
              padding: tokens.space.lg,
              background: tokens.color.bg1,
              border: "1px solid rgba(0,212,170,0.30)",
              borderRadius: tokens.radius.md,
              marginBottom: tokens.space.lg,
            }}
          >
            <div
              style={{
                fontSize: tokens.font.size.xs,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tokens.color.tealLight,
                fontWeight: 600,
                marginBottom: tokens.space.sm,
              }}
            >
              Plaintext X-API-Key
            </div>
            <pre
              style={{
                margin: 0,
                padding: "10px 12px",
                background: tokens.color.bg0,
                color: tokens.color.tealLight,
                borderRadius: tokens.radius.sm,
                fontFamily: tokens.font.mono,
                fontSize: tokens.font.size.sm,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                userSelect: "all",
              }}
            >
              <code>{created.api_key_plaintext}</code>
            </pre>
            <div
              style={{
                marginTop: tokens.space.sm,
                display: "flex",
                gap: tokens.space.sm,
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={() => void onCopy()}
                style={buttonStyle("subtle")}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
              <span
                style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}
              >
                Visible until you sign out or close this tab.
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/debt-lookup/clients/${encodeURIComponent(created.client_id)}`,
                )
              }
              style={buttonStyle("primary")}
            >
              Open client
            </button>
            <button
              type="button"
              onClick={() => navigate("/debt-lookup/clients")}
              style={buttonStyle("ghost")}
            >
              Back to list
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="New mirror client"
        subtitle="Mint an X-API-Key for a Debt Lookup partner"
      />
      <Card>
        <form onSubmit={onSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel htmlFor="new-mirror-client-name">
                Client name
              </FieldLabel>
              <input
                id="new-mirror-client-name"
                required
                value={form.client_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, client_name: e.target.value }))
                }
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-mirror-rps">RPS cap</FieldLabel>
              <input
                id="new-mirror-rps"
                required
                type="number"
                min="1"
                max="500"
                value={form.rps_cap}
                onChange={(e) =>
                  setForm((f) => ({ ...f, rps_cap: Number(e.target.value) }))
                }
                style={inputStyle}
              />
            </div>
          </div>
          {error && (
            <div
              role="alert"
              style={{
                marginTop: tokens.space.lg,
                padding: tokens.space.md,
                background: tokens.color.dangerBg,
                border: `1px solid ${tokens.color.dangerBorder}`,
                color: tokens.color.danger,
                borderRadius: tokens.radius.sm,
                fontSize: tokens.font.size.sm,
              }}
            >
              {error}
            </div>
          )}
          <div
            style={{
              marginTop: tokens.space.lg,
              display: "flex",
              gap: tokens.space.sm,
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              style={buttonStyle("primary", submitting)}
            >
              {submitting ? "Creating…" : "Create client"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/debt-lookup/clients")}
              disabled={submitting}
              style={buttonStyle("ghost", submitting)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
